<template>
	<helio-dropdown
		class="timepicker"
		:close-on-select="true"
		:disabled="disabled"
		:hint="hint"
		:icon-token="icon.token"
		:icon-weight="icon.weight"
		:initial-focus="initialFocus"
		:label="label"
		:options="options"
		:placeholder="placeholder"
		:show-label="showLabel"
		:size="size"
		@changed="updateTime($event)"
	/>
</template>

<script>
export default {
	name: 'Timepicker',

	props: {
		allowPast: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Allows time that is earlier than the current time',
		},

		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Timepicker will be disabled for the user',
		},

		focusedInterval: {
			type: Number,
			required: false,
			default: 780,
			description: 'Interval in minute where we need to be positionned by default when we open the dropdown',
		},

		hasIcon: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets an icon that will appear next to timepicker choices',
		},

		hint: {
			type: String,
			required: false,
			default: null,
			description: 'Sets a hint that will appear below the unopened Timepicker',
		},

		interval: {
			type: Number,
			required: false,
			default: 60,
			validator: value => [60, 30, 15].indexOf(value) !== -1,
			description: 'Interval in minutes between each dropdown choice',
		},

		label: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text of the Timepicker\'s label',
		},

		showLabel: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Set whether we show the label or not',
		},

		size: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'normal',
				'large',
			].indexOf(value) !== -1,
			description: 'Sets the height of the Timepicker component',
		},
	},

	data() {
		return {
			initialFocus: '',
			options: [],
			currentLocale: '',
		};
	},

	computed: {
		icon() {
			return this.hasIcon
				? { token: 'clock', weight: 'light' }
				: { token: null, weight: null };
		},

		placeholder() {
			return this.$i18n.locale === 'fr' ? '0h00' : '0:00 am';
		},
	},

	watch: {
		allowPast() {
			this.setOptions();
		},
	},

	mounted() {
		this.currentLocale = this.$i18n.locale;
		this.setOptions();
	},

	updated() {
		this.$nextTick(() => {
			if (this.currentLocale !== this.$i18n.locale) {
				this.setOptions();
				this.currentLocale = this.$i18n.locale;
			}
		});
	},

	methods: {
		updateTime(value) {
			let time = value[0].label;

			if (this.$i18n.locale === 'fr') {
				time = time.split('h').join(':');
			}

			if (this.$i18n.locale === 'en') {
				const addTwelvehundred = time.includes('pm');
				const amTime = time.slice(0, -3);
				let pmTime = `${parseInt(time.split(':').join('').slice(0, -3), 10) + 1200}`;
				const pmTimeSplit = pmTime.split('');
				pmTimeSplit.splice(2, 0, ':');
				pmTime = pmTimeSplit.join('');

				time = addTwelvehundred
					? pmTime
					: amTime;
			}

			this.$emit('timeChanged', time);
		},

		setOptions() {
			if (this.options.length > 0) {
				this.options = [];
			}

			const date = new Date();
			const { locale } = this.$i18n;
			let intervalMultiple = 1;

			switch (this.interval) {
				case 60:
					intervalMultiple = 1;
					break;
				case 30:
					intervalMultiple = 2;
					break;
				case 15:
					intervalMultiple = 4;
					break;
				default:
					break;
			}

			for (let hour = 0; hour < 24; hour += 1) {
				let minutes = 0;
				for (let interval = 1; interval <= intervalMultiple; interval += 1) {
					date.setHours(hour);
					date.setMinutes(minutes);

					const timeInMinutes = (hour * 60) + minutes;
					let label = null;

					if (locale === 'fr') {
						const formattedDate = date.toLocaleTimeString('fr', { hour: 'numeric', minute: '2-digit' });
						const splittedDate = formattedDate.split(':');
						let currentHours = splittedDate[0];
						const currentMinutes = splittedDate[1];

						// In french we do not want a leading zero
						if (currentHours && currentHours.length > 1 && currentHours.charAt(0) === '0') {
							currentHours = currentHours.substring(1);
						}

						label = `${currentHours}h${currentMinutes}`;
					} else {
						label = date.toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' }).toLowerCase();

						// In english we do want to use 00:XX am instead of 12:XX am
						if (label.includes('12:') && label.includes('am')) {
							label = label.replace('12:', '0:');
						}
					}

					this.options.push({
						label: label,
						value: timeInMinutes,
					});

					minutes += this.interval;
				}

				const initialFocus = this.options.find(option => option.value === this.focusedInterval);

				if (initialFocus) {
					this.initialFocus = initialFocus.label;
				}
			}

			if (!this.allowPast) {
				const currentDate = new Date();
				const currentTime = (currentDate.getHours() * 60) + currentDate.getMinutes();

				this.options = this.options.filter(option => option.value > currentTime);
			}
		},
	},

};
</script>
